import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { Icon, l } from '@manychat/manyui'

import * as flowSimulatorActions from 'common/builder/components/flowSimulator/actions/flowSimulatorActions'
import ToolBtn from 'common/builder/components/flowSimulator/components/toolBtn/ToolBtn'
import * as flowSimulatorSelectors from 'common/builder/components/flowSimulator/selectors/flowSimulatorSelectors'
import { getBuilderById } from 'common/builder/selectors/builder/commonSelectors'
import { alert } from 'common/core'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { analyticsService } from 'utils/services/analytics'
import errorTrackingService from 'utils/services/errorTrackingService'
import { useHotjar } from 'utils/services/hotjar/useHotjar'

import PhoneContainer from '../phoneContainer/PhoneContainer'

import cm from './FlowSimulator.module.css'

const FlowSimulator = ({ builderId }: { builderId: string }) => {
  const dispatch = useAppDispatch()

  const builderData = useAppSelector((state) => getBuilderById(state, builderId))
  const { open: isOpen, simulatorData } = useAppSelector((state) =>
    flowSimulatorSelectors.getFlowSimulatorState(state, builderId),
  )

  const [phoneContainer, setPhoneContainer] = useState<HTMLDivElement | null>(null)
  const [isOverlay, setIsOverlay] = useState(false)

  const simulatorRef = useRef<ReturnType<typeof window.MC_FLOWSIMULATOR.createSimulator>>()

  const logMainProcessingComplete = () => {
    analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.MAIN_PROCESSING_BRANCH_COMPLETE')
  }

  const closeSimulator = useCallback(() => {
    analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.HIDE')

    dispatch(flowSimulatorActions.closeFlowSimulator(builderId))
    simulatorRef.current = undefined
    setIsOverlay(false)
  }, [dispatch, builderId])
  const handleCloseClick = useCallback(() => {
    analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.BTN_CLOSE.CLICK')

    closeSimulator()
  }, [closeSimulator])

  const { hotjarEvent } = useHotjar()

  const handleFullRestart = useCallback(() => {
    const simulatorData = dispatch(
      flowSimulatorActions.openFlowSimulator(builderId),
    ) as unknown as string

    try {
      simulatorRef.current?.restart(JSON.parse(simulatorData))

      setIsOverlay(false)
    } catch (e) {
      errorTrackingService.trackError(e, {
        fingerprint: 'flow_simulator_cant_play_flow',
        extra: { simulatorData },
        tags: {
          FlowSimulator: true,
        },
      })
      analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.ERROR_CANT_PLAY_FLOW')

      hotjarEvent('flow_builder_error')
      alert(l.translate('The Preview cannot be played.'), 'danger')
      console.error(e)
      closeSimulator()
    }
  }, [dispatch, builderId, hotjarEvent, closeSimulator])

  const handleRestartClick = useCallback(() => {
    analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.BTN_RESTART.CLICK')

    const isActual = dispatch(
      flowSimulatorActions.checkFlowSimulatorActual(builderId),
    ) as unknown as boolean
    if (isActual) {
      simulatorRef.current?.restart()
    } else {
      handleFullRestart()
    }
  }, [dispatch, builderId, handleFullRestart])
  const handleRestartFromOverlay = useCallback(() => {
    analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.OVERLAY_OUT_OF_DATE.BTN_RESTART.CLICK')

    handleFullRestart()
  }, [handleFullRestart])

  useEffect(() => {
    if (!isOpen || isOverlay) {
      return
    }
    const isActual = dispatch(
      flowSimulatorActions.checkFlowSimulatorActual(builderId),
    ) as unknown as boolean
    if (!isActual) {
      setIsOverlay(true)
      simulatorRef.current?.showOutOfDateOverlay(handleRestartFromOverlay)
    }
  }, [
    dispatch,
    builderId,
    builderData.state.lastChanged,
    isOpen,
    isOverlay,
    handleRestartFromOverlay,
  ])

  const { title, ava_big } = useAppSelector(getCurrentAccount)

  useEffect(() => {
    if (isOpen && phoneContainer && !simulatorRef.current) {
      const botConfig = {
        name: title,
        avatarUrl: ava_big,
        onMainProcessingComplete: logMainProcessingComplete,
      }

      try {
        const simulator = window.MC_FLOWSIMULATOR.createSimulator(
          phoneContainer,
          JSON.parse(simulatorData as string),
          botConfig,
        )
        simulatorRef.current = simulator

        analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.SHOW')
      } catch (e) {
        errorTrackingService.trackError(e, {
          fingerprint: 'flow_simulator_cant_play_flow',
          extra: { simulatorData },
          tags: {
            FlowSimulator: true,
          },
        })
        analyticsService.sendEvent('FLOW_BUILDER.FLOW_SIMULATOR.ERROR_CANT_PLAY_FLOW')
        hotjarEvent('flow_builder_error')
        alert(l.translate('The Preview cannot be played.'), 'danger')
        console.error(e)
        closeSimulator()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phoneContainer])

  return (
    <>
      <PhoneContainer ref={setPhoneContainer} show={isOpen} />

      {isOpen && (
        <div className={cm.panel}>
          <div className="d-flex flex-col">
            <ToolBtn Icon={Icon.Close} onClick={handleCloseClick} />
            <ToolBtn Icon={Icon.Retry} onClick={handleRestartClick} className="m-t-xs" />
          </div>
        </div>
      )}
    </>
  )
}

export default FlowSimulator
